/**
 * renderMarkdown.ts — shared inline markdown renderer.
 *
 * Extracted from pwa/src/screens/Docs.tsx (STEP-DOCS-PWA-READ-SURFACE) so
 * both the legacy Docs screen and the new v4 DocsSheet can import from one
 * place. No external markdown library — keeps the bundle small.
 */

export function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function inlineFormat(s: string): string {
  // Escape the full input first so plain text cannot inject raw HTML.
  // The markdown regex substitutions below work on already-escaped text and
  // wrap it in safe HTML tags — no inner escHtml needed.
  let r = escHtml(s);
  r = r.replace(/`([^`]+)`/g, (_m, c: string) => `<code>${c}</code>`);
  r = r.replace(/\*\*([^*]+)\*\*/g, (_, c: string) => `<strong>${c}</strong>`);
  r = r.replace(/\*([^*]+)\*/g, (_, c: string) => `<em>${c}</em>`);
  return r;
}

export function renderMarkdown(raw: string): string {
  if (!raw) return "";
  const lines = raw.split("\n");
  const out: string[] = [];
  let inCode = false;
  let codeLines: string[] = [];
  let inList = false;

  for (const line of lines) {
    if (line.startsWith("```")) {
      if (inCode) {
        out.push(`<pre><code>${codeLines.join("\n")}</code></pre>`);
        codeLines = [];
        inCode = false;
      } else {
        if (inList) { out.push("</ul>"); inList = false; }
        inCode = true;
      }
      continue;
    }
    if (inCode) { codeLines.push(escHtml(line)); continue; }
    if (line.startsWith("### ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h4>${escHtml(line.slice(4))}</h4>`);
    } else if (line.startsWith("## ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h3>${escHtml(line.slice(3))}</h3>`);
    } else if (line.startsWith("# ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h2>${escHtml(line.slice(2))}</h2>`);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inlineFormat(line.slice(2))}</li>`);
    } else if (line.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("<br/>");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p>${inlineFormat(line)}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  if (inCode) out.push(`<pre><code>${codeLines.join("\n")}</code></pre>`);
  return out.join("");
}
